/**
 * Hours Approval Service
 *
 * Wraps the hours-approval endpoints on hour-tracked Work Orders — the
 * ones created with "Require Hours" ticked, or carrying the T&M sub-type
 * that forces it on.
 *
 * Those work orders follow this lifecycle:
 *
 *   recorded (per-assignee hours on the create/edit form)
 *     → submitted (stamped by the hand-off / sign-off that declares the
 *       work finished)
 *     → approved (these endpoints)
 *     → billable (the WO can't move to invoiced/closed until then)
 *
 * Approval comes in two flavors, distinguished by `approverType`:
 *
 *   - 'admin'   : an admin or super admin approving on our side. The
 *                 server enforces that the caller actually holds the role.
 *   - 'contact' : the work order's own primary contact — the end customer
 *                 — approving what they're about to be billed for.
 *                 Captured the same way the on-site signature is: a
 *                 manager hands the device over at the job site.
 *
 * Deliberately shaped like estimateApprovalService, but the two sit at
 * opposite ends of a job: that one approves the COST before the work,
 * this one approves the HOURS after it.
 *
 * Backend endpoints:
 *   POST   /api/v1/work-orders/{id}/hours-approval
 *   DELETE /api/v1/work-orders/{id}/hours-approval
 */

import { api } from './api';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * The two valid approver types. The server uses this to decide which
 * approver FK to populate (hours_approved_by_user_id vs
 * hours_approved_by_contact_id).
 */
export type HoursApproverType = 'admin' | 'contact';

/**
 * Body shape posted when approving hours. `contactId` is required when
 * approverType is 'contact'; ignored otherwise.
 */
export interface ApproveHoursPayload {
    approverType: HoursApproverType;
    contactId?: number;
}

/* ============================================
 * SERVICE
 * ============================================ */

export const hoursApprovalService = {
    /** POST /work-orders/{id}/hours-approval */
    approveWorkOrder: (workOrderId: string, payload: ApproveHoursPayload) =>
        api.post(`/work-orders/${workOrderId}/hours-approval`, payload),

    /** DELETE /work-orders/{id}/hours-approval */
    revokeWorkOrder: (workOrderId: string) =>
        api.delete(`/work-orders/${workOrderId}/hours-approval`),

};
